import {
  Alert,
  Box,
  Typography,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import axios, { AxiosError } from "axios";
import { useMutation, useQuery } from "react-query";
import { API_URL } from "../consts";
import { useNavigate, useParams } from "react-router-dom";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { DatePicker } from "@mui/x-date-pickers";
import { memo, useEffect, useState } from "react";
import { Anagrafiche, Clubs, Members } from "../prismaTypes";
import { getString } from "../utils/getString";
import { useSnackbar } from "notistack";
import { Certificato } from "./Certificato";
import { queryClient } from "../App";
import { changeDatesToUTC } from "../utils/dateUtils";
import { Loading } from "../ui/Loading";
import { uiStore } from "../uiStore";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import DeleteIcon from "@mui/icons-material/Delete";
import React from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Link as RouterLink } from "react-router-dom";

const MyGrid = memo((props: { children?: React.ReactNode }) => {
  const { children } = props;
  return (
    <Grid container columnSpacing={1} rowSpacing={1}>
      {children}
    </Grid>
  );
});

const aYearFromNow = new Date();
aYearFromNow.setFullYear(aYearFromNow.getFullYear() + 1);
const defaultValues = {
  id: undefined,
  nome: "",
  cognome: "",
  codice: "",
  tipologiaTariffa: "",
  email: "",
  password: "",
  password2: "",
  certificato: false,
  dataIscrizione: new Date(),
  dataScadenza: aYearFromNow,
  dataBlocco: null,
  sesso: "",
  codiceFiscale: "",
  luogoDiNascita: "",
  statoDiNascita: "",
  dataDiNascita: null,
  indirizzo: "",
  localita: "",
  comune: "",
  provincia: "",
  nazione: "",
  cap: "",
  telefono: "",
  mobile: "",
  telefonoLavoro: "",
  livello: "",
  privacy1: true,
  privacy2: true,
  privacy3: true,
  note: "",
  dataScadenzaCertificato: null,
};

type Inputs = {
  id?: number;
  nome: string;
  cognome: string;
  codice: string;
  tipologiaTariffa: number | string;
  email: string;
  emailVera: string;
  password: string;
  password2: string;
  certificato: boolean;
  dataIscrizione: Date | string | null;
  dataScadenza: Date | string | null;
  dataBlocco: Date | string | null;
  sesso: string;
  codiceFiscale: string;
  luogoDiNascita: string;
  statoDiNascita: string;
  dataDiNascita: Date | string | null;
  indirizzo: string;
  localita: string;
  comune: string;
  provincia: string;
  nazione: string;
  cap: string;
  telefono: string;
  mobile: string;
  telefonoLavoro: string;
  livello: number | string | string;
  privacy1: boolean;
  privacy2: boolean;
  privacy3: boolean;
  note: string;
  dataScadenzaCertificato: Date | string | null;
};

export function CreateUser() {
  let { id } = useParams();
  const [open, setOpen] = React.useState(false);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [loaded, setLoaded] = useState(false);

  const [autoPsw, setAutoPsw] = useState(true);
  const [modPsw, setModPsw] = useState(false);

  const title = id === undefined ? "Crea Nuovo Utente" : "Modifica Utente";
  const setTitle = uiStore((state) => state.setTitle);
  useEffect(() => {
    setTitle(title);
  }, [setTitle, title]);

  const { isLoading: isLoadingClub, data: club } = useQuery<{ club: Clubs }, AxiosError>(
    ["club/getByDomain", window.location.host],
    () => axios.get(API_URL + "club/getByDomain").then((res) => res.data),
    {
      refetchOnWindowFocus: false,
    }
  );

  let dataScadenzaCertificato = yup
    .date()
    .typeError("Data non valida!")
    .nullable()
    .default(undefined)
    .transform((curr, orig) => (orig === "" ? null : curr))
    .label("Data scadenza certificato");

  if (club?.club.certificatoObbligatorio)
    dataScadenzaCertificato = dataScadenzaCertificato.required();

  const schema = yup
    .object({
      nome: yup.string().trim().max(50).required(),
      tipologiaTariffa: yup.number().required("Tipo tariffa è obbligatorio!"),
      cognome: yup.string().trim().max(50).required(),
      codice: yup.string().trim().max(50).required(),
      email: yup.string().trim().label("Username").required(),
      emailVera: yup.string().trim().email().label("Email").required(),
      dataIscrizione: yup
        .date()
        .typeError("Data non valida!")
        .nullable()
        .min(new Date(2000, 0, 0, 0, 0))
        .label("Data Iscrizione")
        .required()
        .default(undefined)
        .transform((curr, orig) => (orig === "" ? null : curr)),
      dataScadenza: yup
        .date()
        .typeError("Data non valida!")
        .nullable()
        .min(yup.ref("dataIscrizione"), "Deve essere maggiore di Data Iscrizione")
        .label("Data Scadenza")
        .required()
        .default(undefined)
        .transform((curr, orig) => (orig === "" ? null : curr)),
      dataBlocco: yup
        .date()
        .typeError("Data non valida!")
        .nullable()
        .default(undefined)
        .transform((curr, orig) => (orig === "" ? null : curr))
        .min(yup.ref("dataIscrizione"))
        .label("Data Blocco"),
      dataDiNascita: yup
        .date()
        .typeError("Data non valida!")
        .nullable()
        .default(undefined)
        .transform((curr, orig) => (orig === "" ? null : curr))
        .label("Data di nascita"),
      codiceFiscale: yup.string().max(16),
      luogoDiNascita: yup.string().max(150),
      statoDiNascita: yup.string().max(150),
      password: yup.string().max(30).label("Password"),
      password2: yup
        .string()
        .label("Conferma Password")
        .oneOf([yup.ref("password"), null], "Deve coincidere con Password"),
      dataScadenzaCertificato,
    })
    .required();

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
    reset,
  } = useForm<Inputs>({
    mode: "onBlur",
    resolver: yupResolver(schema),
  });
  const onSubmit = (data: Inputs) => {
    changeDatesToUTC(data);
    if (id !== undefined) mutateUpdate({ ...data, id: parseInt(id) });
    else mutate(data);
  };

  const {
    isLoading: loadingUser,
    refetch,
    data: apiData,
  } = useQuery<
    {
      member: Members & {
        Anagrafica: Anagrafiche;
      };
    },
    AxiosError
  >(["users/getUser", id], () => axios.get(API_URL + "users/" + id).then((res) => res.data), {
    enabled: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    onSuccess: (data) => {
      const member = data.member;

      setValue("certificato", member.Anagrafica.certificato);
      setValue("nome", member.Anagrafica.nome);
      setValue("cognome", member.Anagrafica.cognome);
      setValue("codice", getString(member.Anagrafica.codiceSocio));
      setValue("email", member.email);
      setValue("emailVera", member.emailVera);
      setValue("dataIscrizione", member.Anagrafica.dataIscrizione);
      setValue("dataScadenza", member.Anagrafica.dataScadenza);
      setValue(
        "dataBlocco",
        member.Anagrafica.dataBlocco === null ? "" : member.Anagrafica.dataBlocco
      );
      setValue("sesso", getString(member.Anagrafica.sesso));
      setValue("codiceFiscale", getString(member.Anagrafica.cf));
      setValue("luogoDiNascita", getString(member.Anagrafica.luogoDiNascita));
      setValue("statoDiNascita", getString(member.Anagrafica.statoDiNascita));
      setValue("indirizzo", getString(member.Anagrafica.indirizzo));
      setValue("localita", getString(member.Anagrafica.localita));
      setValue("comune", getString(member.Anagrafica.comune));
      setValue("provincia", getString(member.Anagrafica.provincia));
      setValue("nazione", getString(member.Anagrafica.nazione));
      setValue("cap", getString(member.Anagrafica.cap));
      setValue("telefono", getString(member.Anagrafica.telefono));
      setValue("mobile", getString(member.Anagrafica.mobile));
      setValue("telefonoLavoro", getString(member.Anagrafica.telefonoLavoro));
      setValue("livello", member.Anagrafica.skill === null ? "" : member.Anagrafica.skill);
      setValue("privacy1", member.Anagrafica.privacy1);
      setValue("privacy2", member.Anagrafica.privacy2);
      setValue("privacy3", member.Anagrafica.privacy3);
      setValue("note", getString(member.Anagrafica.note));
      setValue(
        "dataDiNascita",
        member.Anagrafica.dataDiNascita === null ? "" : member.Anagrafica.dataDiNascita
      );

      setValue("tipologiaTariffa", member.Anagrafica.Tipologia_AssociazioniId);
      setValue(
        "dataScadenzaCertificato",
        member.Anagrafica.dataScadenzaCertificato === null
          ? ""
          : member.Anagrafica.dataScadenzaCertificato
      );

      setLoaded(true);
    },
    onError: (error: AxiosError) => {
      console.error(error);
      enqueueSnackbar("Si è verificato un errore!", {
        variant: "error",
      });
    },
  });

  const { error, mutate } = useMutation(
    (data: Inputs) => {
      return axios.post(API_URL + "users/register", data);
    },
    {
      onSuccess: (data) => {
        queryClient.clear();
        const { id } = data.data;
        if (id) {
          queryClient.invalidateQueries();
          enqueueSnackbar("Utente Creato", { variant: "success" });
          if ("invioEmail" in data.data && !data.data.invioEmail)
            enqueueSnackbar("Invio Email fallito!", { variant: "error" });

          navigate("/app/editUser/" + id);
        }
      },
      onError: (error: AxiosError) => {
        //queryClient.clear();
        enqueueSnackbar(error?.response?.data?.message ?? "Si è verificato un errore!", {
          variant: "error",
        });
      },
    }
  );

  const { mutate: elimina } = useMutation(
    () => {
      return axios.delete(API_URL + "users/" + id);
    },
    {
      onSuccess: () => {
        queryClient.clear();
        enqueueSnackbar("Utente Eliminato", { variant: "success" });
        navigate("/app/users/");
      },
      onError: (error: AxiosError) => {
        queryClient.clear();
        enqueueSnackbar(error?.response?.data?.message ?? "Si è verificato un errore!", {
          variant: "error",
        });
      },
    }
  );

  const { mutate: ripristina } = useMutation(
    () => {
      return axios.post(API_URL + "users/ripristina/" + id);
    },
    {
      onSuccess: () => {
        queryClient.clear();
        enqueueSnackbar("Utente Ripristinato", { variant: "success" });
        navigate("/app/editUser/" + id);
      },
      onError: (error: AxiosError) => {
        queryClient.clear();
        enqueueSnackbar(error?.response?.data?.message ?? "Si è verificato un errore!", {
          variant: "error",
        });
      },
    }
  );

  const { error: errorUpdate, mutate: mutateUpdate } = useMutation(
    (data: Inputs) => {
      const dataBlocco = data.dataBlocco === "" ? null : data.dataBlocco;
      const dataDiNascita = data.dataDiNascita === "" ? null : data.dataDiNascita;
      return axios.post(API_URL + "users/update", { ...data, dataBlocco, dataDiNascita });
    },
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries();
        enqueueSnackbar("Utente Aggiornato", { variant: "success" });
      },
      onError: (error: AxiosError) => {
        queryClient.invalidateQueries();
        enqueueSnackbar(error?.response?.data?.message ?? "Si è verificato un errore!", {
          variant: "error",
        });
      },
    }
  );

  useEffect(() => {
    setLoaded(false);
    if (id === undefined) {
      reset(defaultValues);
      setLoaded(true);
    } else refetch();
  }, [reset, setValue, id, refetch]);

  const [animations, setAnimations] = useState(false);
  useEffect(() => {
    let timeout = 0;
    if (loaded) {
      timeout = window.setTimeout(() => setAnimations(true), 1);
    }
    return () => {
      if (timeout !== 0) clearTimeout(timeout);
    };
  }, [loaded]);

  const onUploadCertificato = () => {
    setValue("certificato", true);
  };
  const { isLoading: isLoadingAss, data: tipiAss } = useQuery<
    { tipologie: { id: number; denominazione: string }[] },
    AxiosError
  >(
    ["getTipologiaAssociazioni"],
    () => axios.get(API_URL + "club/getTipologiaAssociazioni").then((res) => res.data),
    {
      refetchOnWindowFocus: false,
      staleTime: 1e5,
      cacheTime: 1e5,
      onError: (error: AxiosError) => {
        enqueueSnackbar(error?.response?.data?.message ?? "Si è verificato un errore!", {
          variant: "error",
        });
      },
    }
  );

  if (!loaded || isLoadingClub || isLoadingAss || loadingUser) return <Loading />;

  let errorMsg = "";
  if (error) errorMsg = error?.response?.data?.message;
  if (errorUpdate) errorMsg = errorUpdate?.response?.data?.message;

  const eliminaModalClick = () => {
    elimina();
    handleClose();
  };

  return (
    <div className={animations ? "" : "noAnimation"}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <MyGrid>
          <Grid item xs={12} md={5} lg={4}>
            <Controller
              name="nome"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  fullWidth
                  variant="standard"
                  label="Nome"
                  error={Boolean(errors.nome)}
                  helperText={errors.nome?.message ?? ""}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={5} lg={4}>
            <Controller
              name="cognome"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  fullWidth
                  variant="standard"
                  label="Cognome"
                  error={Boolean(errors.cognome)}
                  helperText={errors.cognome?.message ?? ""}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={2} lg={3}>
            <Controller
              name="codice"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <Box sx={{ display: "flex", alignItems: "baseline" }}>
                  <CreditCardIcon sx={{ color: "action.active", mr: 1, my: 0.5 }} />{" "}
                  <TextField
                    {...field}
                    required
                    fullWidth
                    variant="standard"
                    label="Codice Socio"
                    error={Boolean(errors.codice)}
                    helperText={errors.codice?.message ?? ""}
                  />
                </Box>
              )}
            />
          </Grid>
          <Grid item xs={12} md={3} lg={2}>
            <Controller
              name="tipologiaTariffa"
              control={control}
              defaultValue={1}
              render={({ field }) => (
                <FormControl {...field} fullWidth variant="standard">
                  <InputLabel>Tipologia Tariffa</InputLabel>
                  <Select
                    label="Tipologia Tariffa"
                    required
                    value={field.value}
                    onChange={(e) => setValue("tipologiaTariffa", parseInt("" + e.target.value))}
                  >
                    {tipiAss?.tipologie.map((tip) => (
                      <MenuItem key={tip.id} value={tip.id}>
                        {tip.denominazione}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </Grid>
          <Grid item xs={12} md={9} lg={5}>
            <Controller
              name="email"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <Box sx={{ display: "flex", alignItems: "baseline" }}>
                  <TextField
                    {...field}
                    required
                    fullWidth
                    variant="standard"
                    type="text"
                    label="Username"
                    error={Boolean(errors.email)}
                    helperText={errors.email?.message ?? ""}
                  />
                </Box>
              )}
            />
          </Grid>
          <Grid item xs={12} md={12} lg={5}>
            <Controller
              name="emailVera"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <Box sx={{ display: "flex", alignItems: "baseline" }}>
                  <EmailOutlinedIcon sx={{ color: "action.active", mr: 1, my: 0.5 }} />
                  <TextField
                    {...field}
                    required
                    fullWidth
                    variant="standard"
                    type="email"
                    label="Email"
                    error={Boolean(errors.emailVera)}
                    helperText={errors.emailVera?.message ?? ""}
                  />
                </Box>
              )}
            />
          </Grid>
          <Grid item xs={12} md={12} lg={5}>
            <Controller
              name="codiceFiscale"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  variant="standard"
                  label="Codice Fiscale"
                  error={Boolean(errors.codiceFiscale)}
                  helperText={errors.codiceFiscale?.message ?? ""}
                />
              )}
            />
          </Grid>
          {!id && (
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox checked={autoPsw} onChange={(v) => setAutoPsw(v.target.checked)} />
                }
                label="Genera password automatica"
              />
            </Grid>
          )}
          {id && (
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox checked={modPsw} onChange={(v) => setModPsw(v.target.checked)} />
                }
                label="Modifica Password"
              />
            </Grid>
          )}
          {((!id && !autoPsw) || (id && modPsw)) && (
            <>
              <Grid item xs={12} md={6} lg={5}>
                <Controller
                  name="password"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <LockOutlinedIcon sx={{ color: "action.active", mr: 1, my: 0.5 }} />
                      <TextField
                        {...field}
                        fullWidth
                        variant="standard"
                        type="password"
                        label="Password"
                        error={Boolean(errors.password)}
                        helperText={errors.password?.message ?? ""}
                      />
                    </Box>
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6} lg={5}>
                <Controller
                  name="password2"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <LockOutlinedIcon sx={{ color: "action.active", mr: 1, my: 0.5 }} />
                      <TextField
                        {...field}
                        fullWidth
                        variant="standard"
                        type="password"
                        label="Conferma Password"
                        error={Boolean(errors.password2)}
                        helperText={errors.password2?.message ?? ""}
                      />
                    </Box>
                  )}
                />
              </Grid>
            </>
          )}
          <Grid item xs={12} md={4}>
            <Controller
              name="dataIscrizione"
              control={control}
              render={({ field }) => (
                <DatePicker
                  {...field}
                  mask={"__/__/____"}
                  label="Data Iscrizione"
                  onChange={field.onChange}
                  value={field.value}
                  renderInput={(params) => (
                    <TextField
                      required
                      variant="standard"
                      helperText={errors.dataIscrizione?.message ?? ""}
                      {...params}
                      error={Boolean(errors.dataIscrizione)}
                    />
                  )}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Controller
              name="dataScadenza"
              control={control}
              render={({ field }) => (
                <DatePicker
                  {...field}
                  label="Data Scadenza"
                  mask={"__/__/____"}
                  onChange={field.onChange}
                  value={field.value}
                  renderInput={(params) => (
                    <TextField
                      required
                      variant="standard"
                      helperText={errors.dataScadenza?.message ?? ""}
                      {...params}
                      error={Boolean(errors.dataScadenza)}
                    />
                  )}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Controller
              name="dataBlocco"
              control={control}
              render={({ field }) => (
                <DatePicker
                  {...field}
                  label="Data Blocco"
                  mask={"__/__/____"}
                  onChange={field.onChange}
                  value={field.value}
                  renderInput={(params) => (
                    <TextField
                      variant="standard"
                      helperText={errors.dataBlocco?.message ?? ""}
                      {...params}
                      error={Boolean(errors.dataBlocco)}
                    />
                  )}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="privacy1"
              control={control}
              defaultValue={false}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={field.value}
                      onChange={(v) => setValue("privacy1", v.target.checked)}
                    />
                  }
                  label={<>Accettazione privacy (fornitura del servizio)</>}
                />
              )}
            />
            {/* </Grid>
          <Grid item xs={12}> */}
            <Controller
              name="privacy2"
              control={control}
              defaultValue={false}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={field.value}
                      onChange={(v) => setValue("privacy2", v.target.checked)}
                    />
                  }
                  label={<>Accettazione privacy (comunicazioni commerciali)</>}
                />
              )}
            />
            {/* </Grid>
          <Grid item xs={12}> */}
            <Controller
              name="privacy3"
              control={control}
              defaultValue={false}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={field.value}
                      onChange={(v) => setValue("privacy3", v.target.checked)}
                    />
                  }
                  label={<>Accettazione privacy (comunicazioni da terzi)</>}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="note"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  multiline
                  rows={2}
                  variant="standard"
                  label="Note"
                  error={Boolean(errors.note)}
                  helperText={errors.note?.message ?? ""}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Controller
              name="certificato"
              control={control}
              defaultValue={false}
              render={({ field }) => (
                <FormControlLabel
                  {...field}
                  control={
                    <Checkbox
                      checked={field.value}
                      onChange={(v) => setValue("certificato", v.target.checked)}
                    />
                  }
                  label="Certificato Medico Presente"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Controller
              name="dataScadenzaCertificato"
              control={control}
              defaultValue={new Date()}
              render={({ field }) => (
                <DatePicker
                  {...field}
                  label="Scadenza Certificato"
                  mask={"__/__/____"}
                  onChange={field.onChange}
                  value={field.value}
                  renderInput={(params) => (
                    <TextField
                      variant="standard"
                      helperText={errors.dataScadenzaCertificato?.message ?? ""}
                      {...params}
                      error={Boolean(errors.dataScadenzaCertificato)}
                    />
                  )}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Controller
              name="livello"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  type={"number"}
                  variant="standard"
                  label="Livello"
                  error={Boolean(errors.livello)}
                  helperText={errors.livello?.message ?? ""}
                />
              )}
            />
          </Grid>
        </MyGrid>

        <Divider sx={{ mt: 1 }} />
        <Typography variant="h3" mt={1} mb={1}>
          Anagrafica
        </Typography>

        <MyGrid>
          <Grid item xs={12} md={4}>
            <Controller
              name="sesso"
              control={control}
              render={({ field }) => (
                <FormControl {...field}>
                  <FormLabel>Sesso</FormLabel>
                  <RadioGroup
                    name="sesso"
                    row
                    value={field?.value ?? null}
                    onChange={(e) => setValue("sesso", e.target.value)}
                  >
                    <FormControlLabel value="F" control={<Radio />} label="Femmina" />
                    <FormControlLabel value="M" control={<Radio />} label="Maschio" />
                  </RadioGroup>
                </FormControl>
              )}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Controller
              name="dataDiNascita"
              control={control}
              render={({ field }) => (
                <DatePicker
                  {...field}
                  mask={"__/__/____"}
                  label="Data di Nascita"
                  onChange={field.onChange}
                  value={field.value}
                  renderInput={(params) => (
                    <TextField
                      variant="standard"
                      helperText={errors.dataDiNascita?.message ?? ""}
                      {...params}
                      error={Boolean(errors.dataDiNascita)}
                    />
                  )}
                />
              )}
            />
          </Grid>
        </MyGrid>

        <MyGrid>
          <Grid item xs={12} md={4}>
            <Controller
              name="luogoDiNascita"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  variant="standard"
                  label="Luogo di Nascita"
                  error={Boolean(errors.luogoDiNascita)}
                  helperText={errors.luogoDiNascita?.message ?? ""}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Controller
              name="statoDiNascita"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  variant="standard"
                  label="Stato di Nascita"
                  error={Boolean(errors.statoDiNascita)}
                  helperText={errors.statoDiNascita?.message ?? ""}
                />
              )}
            />
          </Grid>
        </MyGrid>

        <MyGrid>
          <Grid item xs={12} md={4}>
            <Controller
              name="indirizzo"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  variant="standard"
                  label="Indirizzo"
                  error={Boolean(errors.indirizzo)}
                  helperText={errors.indirizzo?.message ?? ""}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Controller
              name="localita"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  variant="standard"
                  label="Localita"
                  error={Boolean(errors.localita)}
                  helperText={errors.localita?.message ?? ""}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Controller
              name="comune"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  variant="standard"
                  label="Comune"
                  error={Boolean(errors.comune)}
                  helperText={errors.comune?.message ?? ""}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Controller
              name="provincia"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  variant="standard"
                  label="Provincia"
                  error={Boolean(errors.provincia)}
                  helperText={errors.provincia?.message ?? ""}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Controller
              name="nazione"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  variant="standard"
                  label="Nazione"
                  error={Boolean(errors.nazione)}
                  helperText={errors.nazione?.message ?? ""}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Controller
              name="cap"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  variant="standard"
                  label="Cap"
                  error={Boolean(errors.cap)}
                  helperText={errors.cap?.message ?? ""}
                />
              )}
            />
          </Grid>
        </MyGrid>

        <MyGrid>
          <Grid item xs={12} md={4}>
            <Controller
              name="telefono"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  type={"tel"}
                  variant="standard"
                  label="Telefono"
                  error={Boolean(errors.telefono)}
                  helperText={errors.telefono?.message ?? ""}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Controller
              name="mobile"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  type={"tel"}
                  variant="standard"
                  label="Mobile"
                  error={Boolean(errors.mobile)}
                  helperText={errors.mobile?.message ?? ""}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Controller
              name="telefonoLavoro"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  type={"tel"}
                  variant="standard"
                  label="Telefono Lavoro"
                  error={Boolean(errors.telefonoLavoro)}
                  helperText={errors.telefonoLavoro?.message ?? ""}
                />
              )}
            />
          </Grid>
        </MyGrid>

        <Stack direction="row" gap={2} sx={{ mt: 2 }}>
          <Button variant="outlined" component={RouterLink} to="/app/users">
            Ritorna <ArrowBackIcon />
          </Button>

          {!id && (
            <Button type="submit" variant="contained">
              Crea Giocatore
            </Button>
          )}

          {id !== undefined && (
            <>
              <Button type="submit" variant="contained">
                Modifica Utente
              </Button>
              {!apiData?.member.deleted && (
                <Button variant="contained" color="error" onClick={() => handleClickOpen()}>
                  <DeleteIcon />
                  Elimina
                </Button>
              )}
              {apiData?.member.deleted && (
                <Button variant="contained" color="secondary" onClick={() => ripristina()}>
                  Ripristina
                </Button>
              )}
            </>
          )}

          {id !== undefined && (
            <Certificato
              userId={id}
              nome={getValues("nome")}
              cognome={getValues("cognome")}
              onUpload={onUploadCertificato}
            />
          )}
        </Stack>

        {errorMsg && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {errorMsg}
          </Alert>
        )}
        <Stack gap={1} sx={{ mt: 2 }}>
          {Object.entries(errors).map((val) => (
            <Alert key={val[0]} severity="error">
              {val[1].message}
            </Alert>
          ))}
        </Stack>
      </form>
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>{"Elimina giocatore?"}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Sei sicuro di voler eliminare il giocatore? <br />
            Verranno eliminati conti, card, prenotazioni. <br />
            <Typography fontWeight="600">L'operazione non è annullabile.</Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>annulla</Button>
          <Button color="error" onClick={eliminaModalClick} autoFocus>
            Elimina
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
